import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useState } from 'react';

import { Spacing, Typography } from 'chatie-design-system';

import Badge, { BadgeProps } from '@components/common/Badge';
import { LinkItem } from '@components/common/ListItem';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import FreeChatEpisodeSelectModal from '@components/modal/FreeChatEpisodeSelectModal';

import { StoryTheme } from '@constants';
import { IStoryAd, IStoryDetail } from '@model';
import { media } from '@styles/css';
import { useAnalytics } from '@utils/analytics';

import { SRStoryCard } from './Story';

const containerCSS = css`
  display: grid;
  gap: 0;

  ${media.tablet} {
    grid-template-columns: repeat(3, 1fr);
    padding: 0px ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    grid-template-columns: repeat(4, 1fr);
    padding: 0px ${Spacing.xxlarge}px;
  }

  & > * {
    min-width: 0;
    overflow: hidden;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const freeChatCtaCSS = (_theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 14px;
  background: linear-gradient(
    56.96deg,
    rgba(4, 200, 208, 0.12) 19.7%,
    rgba(21, 144, 212, 0.12) 80.3%
  );
  cursor: pointer;
  margin: 6px ${Spacing.medium}px 4px;

  span {
    ${Typography.body12};
    font-weight: 600;
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    white-space: nowrap;
  }
`;

type SRViewListPorps = {
  isShowAd: boolean;
  ad?: IStoryAd;
  badgePreset: Pick<BadgeProps, 'color' | 'backgroundColor' | 'border'>;
  curation: IStoryDetail[];
  showNativeAd?: boolean;
  showFreeChatCta?: boolean;
};

const SRViewList = ({
  isShowAd,
  ad,
  badgePreset,
  curation,
  showNativeAd = false,
  showFreeChatCta = false,
}: SRViewListPorps) => {
  const { firebase } = useAnalytics();
  const { t } = useTranslation();
  const [selectedStory, setSelectedStory] = useState<IStoryDetail | null>(null);
  const [isFreeChatModalOpen, setFreeChatModalOpen] = useState(false);

  return (
    <div css={containerCSS}>
      {isShowAd && !!ad?.story && (
        <LinkItem
          newTab
          hideArrow
          to={`/stories/${ad.story.id}`}
          onClick={() => {
            firebase.logEvent('story_ad_click', {
              storyId: ad.story.id,
              storyAdId: ad.id,
              where: 'curation',
            });
          }}
          left={
            <SRStoryCard
              {...{ ...ad.story }}
              label={<Badge text={t('홍보')} {...badgePreset} />}
            />
          }
        />
      )}
      {curation?.map((item, index) => {
        return (
          <div key={item.id + index.toString() + 'SRViewList'}>
            <LinkItem
              newTab
              to={
                item.theme === StoryTheme.iknow
                  ? `/stories/${item.id}/licensed`
                  : `/stories/${item.id}`
              }
              hideArrow
              left={<SRStoryCard {...{ ...item }} rank={index + 1} />}
            />
            {showFreeChatCta && (
              <div
                css={freeChatCtaCSS}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStory(item);
                  setFreeChatModalOpen(true);
                }}
              >
                <span>✨ {t('무제한 무료! 작품 속 캐릭터와 대화하기')}</span>
              </div>
            )}
          </div>
        );
      })}
      {showNativeAd && <NativeInFeedAd unitKey="home_sr_mobile" />}
      <FreeChatEpisodeSelectModal
        name="free-chat-episode-select-modal"
        open={isFreeChatModalOpen}
        onClose={() => setFreeChatModalOpen(false)}
        story={selectedStory}
      />
    </div>
  );
};

export default SRViewList;
